/**
 * BookPage
 * The Prompt-Brush 1.0 book + ways to support the project.
 * Reached from the book icon in the bottom-right corner.
 *
 * Purchase links are hardcoded to the correct ISBN-9781797236636 listings
 * so they always work (they don't depend on editable site content).
 *
 * Typography: Delcan Mono only at one size (DELCAN_SIZE); long-form book
 * copy is set in the serif; small labels/buttons in FK Grotesk Mono.
 *
 * Book photo lives at public/book-cover.jpg.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

const MONO = 'FK Grotesk Mono, monospace';
const DISPLAY = 'Delcan Mono, monospace';
const SERIF = 'Martina Plantijn, Georgia, serif';
const DELCAN_SIZE = 'text-2xl';

const BUY_LINKS = [
  { label: 'Buy from the studio · $30', href: 'https://delcan.co/shop/prompt-brush-1-0-book?utm_source=prompt-brush.com&utm_medium=referral' },
  { label: 'Buy on Amazon', href: 'https://www.amazon.com/Prompt-Brush-1-0-First-Non-AI-Generative/dp/1797236636' },
  { label: 'Buy on Bookshop', href: 'https://bookshop.org/p/books/prompt-brush-1-0-the-first-non-ai-generative-art-model-pablo-delcan/37bcd4c4c4768218' },
  { label: 'Buy at Barnes & Noble', href: 'https://www.barnesandnoble.com/w/prompt-brush-10-pablo-delcan/1146793438?ean=9781797236636' },
];

function BookCover() {
  const [errored, setErrored] = useState(false);

  if (errored) {
    return (
      <div
        className="w-full rounded-sm border border-border bg-card flex flex-col items-center justify-center text-center px-6"
        style={{ aspectRatio: '5/7' }}
      >
        <div className={`text-foreground ${DELCAN_SIZE} mb-3`} style={{ fontFamily: DISPLAY }}>
          Prompt-Brush 1.0
        </div>
        <div className="text-muted-foreground uppercase tracking-widest" style={{ fontSize: '9pt', fontFamily: MONO }}>
          Add a photo at /public/book-cover.jpg
        </div>
      </div>
    );
  }

  return (
    <img
      src="/book-cover.jpg"
      alt="Prompt-Brush 1.0 — The First Non-AI Generative Art Model by Pablo Delcan"
      className="w-full h-auto rounded-sm shadow-2xl"
      onError={() => setErrored(true)}
    />
  );
}

function BuyLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="block w-full text-center border border-border rounded-full px-6 py-3 text-foreground hover:bg-foreground hover:text-background transition-colors"
      style={{ fontFamily: MONO, fontSize: '11pt' }}
    >
      {label}
    </a>
  );
}

export function BookPage() {
  return (
    <div className="h-screen overflow-y-auto">
      <div className="container mx-auto px-4 py-16 md:py-24 max-w-6xl">
        {/* ===== Hero ===== */}
        <div className="grid md:grid-cols-[3fr_2fr] gap-10 md:gap-16 items-center">
          <div className="max-w-sm sm:max-w-md md:max-w-none mx-auto w-full">
            <BookCover />
          </div>

          <div>
            <div
              className="text-muted-foreground uppercase tracking-widest mb-4"
              style={{ fontSize: '9pt', fontFamily: MONO }}
            >
              The Book
            </div>

            <h1 className={`text-foreground ${DELCAN_SIZE} leading-tight mb-2`} style={{ fontFamily: DISPLAY }}>
              <Link to="/collection" className="hover:underline decoration-1 underline-offset-4" title="Browse all 1,541 drawings">
                Prompt-Brush 1.0
              </Link>
            </h1>

            <p className="text-foreground leading-snug mb-3" style={{ fontFamily: MONO, fontSize: '13pt' }}>
              The First Non-AI Generative Art Model
            </p>

            <p
              className="text-muted-foreground uppercase tracking-widest mb-6"
              style={{ fontSize: '9pt', fontFamily: MONO }}
            >
              by Pablo Delcan · Hardcover · ★ 4.2 (21)
            </p>

            <p className="text-foreground leading-relaxed mb-8 max-w-md" style={{ fontFamily: SERIF, fontSize: '15pt' }}>
              In response to the rise of AI-generated art, designer Pablo Delcan takes a
              human-centered approach in this interpretive, illustrative collection of
              humorous, poignant, and universal ideas based on text prompts from the public.
            </p>

            <div className="space-y-3 max-w-xs">
              {BUY_LINKS.map((l) => (
                <BuyLink key={l.href} href={l.href} label={l.label} />
              ))}
            </div>

            <Link
              to="/collection"
              className="inline-flex items-center gap-2 mt-6 text-foreground hover:underline decoration-1 underline-offset-4"
              style={{ fontFamily: MONO, fontSize: '11pt' }}
            >
              Or browse all 1,541 drawings free →
            </Link>
          </div>
        </div>

        {/* ===== About the book ===== */}
        <div className="mt-24 max-w-2xl">
          <div
            className="text-muted-foreground uppercase tracking-widest mb-6"
            style={{ fontSize: '9pt', fontFamily: MONO }}
          >
            About the book
          </div>

          <div className="space-y-6 text-foreground leading-relaxed" style={{ fontFamily: SERIF, fontSize: '15pt' }}>
            <p>
              Unlike AI tools that automatically generate art based on text prompts,{' '}
              <Link to="/collection" className="underline decoration-1 underline-offset-2 hover:opacity-70">Prompt-Brush 1.0</Link>{' '}
              celebrates the creativity and sensibility of the artist's work. Pablo Delcan invited the
              public to submit prompts, which he then manually interpreted and translated into images —
              a curated selection of submitted prompts and original artwork, every piece with a unique,
              human touch.
            </p>
            <p>
              In Delcan's quirky and clever sensibility, prompts like “5 Second Rule” become an ice cream
              cone embedded with cigarette butts and debris. His hand-drawn interpretations emphasize the
              value of human creativity and effort over the speed and efficiency of AI models. The
              simplicity of the black ink drawings is direct and childlike — a refreshing departure from
              the increasingly detailed, hyperrealistic outputs of AI art generators.
            </p>
          </div>

          <blockquote
            className="border-l-2 border-foreground pl-5 my-10 text-foreground"
            style={{ fontFamily: SERIF, fontSize: '20pt', lineHeight: 1.3 }}
          >
            “Infinitely more layered than its AI-generated counterpart.”
            <span className="block mt-2 text-muted-foreground uppercase tracking-widest" style={{ fontSize: '9pt', fontFamily: MONO }}>
              Fast Company
            </span>
          </blockquote>

          <p className="text-foreground leading-relaxed" style={{ fontFamily: SERIF, fontSize: '15pt' }}>
            This handsome slipcased volume makes an outstanding gift for artists and art advocates,
            graphic designers, creative professionals, educators, students, and anyone who values the
            creative process and is wary of AI's impact on the art world.
          </p>
        </div>

        {/* ===== About the author ===== */}
        <div className="mt-24 max-w-2xl">
          <div className="text-muted-foreground uppercase tracking-widest mb-6" style={{ fontSize: '9pt', fontFamily: MONO }}>
            About the author
          </div>
          <div className="space-y-6 text-foreground leading-relaxed" style={{ fontFamily: SERIF, fontSize: '15pt' }}>
            <p>
              Pablo Delcan is a graphic designer and art director originally from the island of Menorca,
              Spain. He is a visual contributor to The New York Times, where he communicates complex ideas
              through compelling imagery — designing numerous covers for The New York Times Magazine and
              illustrating hundreds of op-eds, some for noted figures such as Joe Biden, Mikhail Gorbachev,
              and John McWhorter, including the anonymous “I Am Part of the Resistance Inside the Trump
              Administration” op-ed that became a political sensation in 2018.
            </p>
            <p>
              His work has appeared in Le Monde, The Washington Post, and The Wall Street Journal. Forbes
              included him in their “30 Under 30 in Art and Style,” and he was honored as a Young Gun by the
              Art Directors Club in 2016. He lives with his young family in upstate New York, where he works
              out of his studio in a barn marked with large letters that state “THIS PLACE.”
            </p>
          </div>
        </div>

        {/* ===== The store =====
            This used to be a second storefront: a three-card grid selling the
            giclee prints and the S.V.A. poster, with a "coming soon" card for
            stickers. It duplicated /shop with the same Stripe links, went stale
            the moment anything was added there (it never learned about the
            zine), and split buying across two pages. One line, one destination.
        */}
        <div className="mt-24 pt-12 border-t border-border">
          <div className="text-muted-foreground uppercase tracking-widest mb-3" style={{ fontSize: '9pt', fontFamily: MONO }}>
            Support the project
          </div>
          <h2 className={`text-foreground ${DELCAN_SIZE} mb-4`} style={{ fontFamily: DISPLAY }}>
            Take a piece home
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-8 max-w-2xl" style={{ fontFamily: MONO, fontSize: '11pt' }}>
            Prompt-Brush is an independent project. The zine, archival giclée prints of any of the
            1,541 drawings, and the signed S.V.A. subway poster are all in the store — every order
            helps keep it going.
          </p>
          <Link
            to="/shop"
            className="inline-block rounded-full px-8 py-3 bg-foreground text-background hover:opacity-80 transition-opacity"
            style={{ fontFamily: MONO, fontSize: '11pt' }}
          >
            Visit the store &rarr;
          </Link>
        </div>
      </div>
    </div>
  );
}
